/**
 * @module slideshow-loader
 * Manages video players (Vimeo, YouTube, local) to add functionality
 * like fade-in on play. It dynamically loads the YouTube Iframe API if needed.
 */

// --- YouTube API Loader ---
let youtubeApiReady = false;
let youtubeApiLoading = false;
const youtubeCallbacks = [];

function loadYouTubeApi() {
    if (youtubeApiReady || youtubeApiLoading) return;

    youtubeApiLoading = true;
    const tag = document.createElement('script');
    tag.src = "https://www.youtube.com/iframe_api";
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

    window.onYouTubeIframeAPIReady = () => {
        youtubeApiReady = true;
        youtubeApiLoading = false;
        youtubeCallbacks.forEach(callback => callback());
        youtubeCallbacks.length = 0; // Clear callbacks after execution
    };
}

function onYouTubeApiReady(callback) {
    if (youtubeApiReady) {
        callback();
    } else {
        youtubeCallbacks.push(callback);
        if (!youtubeApiLoading) {
            loadYouTubeApi();
        }
    }
}

/**
 * Sets up an IntersectionObserver to play/pause a video when it enters/leaves the viewport.
 * @param {HTMLElement} element - The element to observe.
 * @param {Function} playFn - The function to call when the element is intersecting.
 * @param {Function} pauseFn - The function to call when the element is not intersecting.
 */
function setupViewportObserver(element, playFn, pauseFn) {
    const observerOptions = {
        root: null, // relative to the viewport
        threshold: 0, // trigger as soon as any part of the element is visible
    };

    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                playFn();
            } else {
                pauseFn();
            }
        });
    }, observerOptions);

    observer.observe(element);
}

/**
 * Initializes video players within a given scope, handling hover-to-play based on the
 * `data-display="hover"` attribute of the video container.
 * @param {HTMLElement|Document} [scope=document] - The element to search within for videos.
 */
export function initializeVideoPlayers(scope = document) {
    const videoContainers = scope.querySelectorAll('.video-container');

    videoContainers.forEach(container => {
        const iframe = container.querySelector('iframe');  // Potential iframe video
        const video = container.querySelector('video');    // Potential HTML5 video element
        const mediaWrapper = container.closest('.media'); // Get the main .media wrapper
        if (!mediaWrapper) {
            return; // If not inside a .media element, skip.
        }
        const isHoverPlay = mediaWrapper.dataset.videoDisplay === 'hover';  // Check for hover play attribute on the wrapper
        const isInViewPlay = mediaWrapper.dataset.videoDisplay === 'inview';

        // For hover-play, we need to find the true hoverable parent, which might be a card or listing item.
        const hoverTarget = mediaWrapper.closest('.media-target') || mediaWrapper;

        // --- Handle Iframe Videos (Vimeo & YouTube) ---
        if (iframe) {
          // --- Vimeo Iframe ---
          if (iframe.src.includes('vimeo.com') && typeof window.Vimeo !== 'undefined') {
              const player = new window.Vimeo.Player(iframe);
              
              // Set the data-playing attribute as soon as the player is ready to minimize visual delay
              player.ready().then(() => {
                   // If it's an autoplay video (not hover-play), assume it will start playing
                   // and show it immediately to avoid a black flash.
                   if (!isHoverPlay) {
                       iframe.setAttribute('data-playing', 'true');
                   }
              });

              // Check initial state in case it's already playing (handles race condition for autoplay)
              player.getPaused().then(paused => {
                  if (!paused) {
                      iframe.setAttribute('data-playing', 'true');
                  }
              }).catch(() => {
                  // Ignore errors if player isn't ready, the event listener will catch it.
              });

              player.on('play', () => iframe.setAttribute('data-playing', 'true'));
              player.on('pause', () => iframe.removeAttribute('data-playing'));
              // Also handle the 'ended' state for non-looping videos
              player.on('ended', () => iframe.removeAttribute('data-playing'));

              if (isHoverPlay) {
                  let pauseTimeout;
                  
                  hoverTarget.addEventListener('mouseenter', () => {
                      clearTimeout(pauseTimeout);
                      player.play();
                  });
                  hoverTarget.addEventListener('mouseleave', () => {
                      iframe.removeAttribute('data-playing');
                      pauseTimeout = setTimeout(() => player.pause(), 1000);
                  });
                  player.pause();
                  iframe.removeAttribute('data-playing');
              } else if (isInViewPlay) {
                  player.setVolume(0);
                  setupViewportObserver(
                      container,
                      () => player.play().catch(() => {}),
                      () => player.pause().catch(() => {})
                  );
              } else {
                  // For autoplaying background videos, PHP adds autoplay=1.
                  // The initial state check and event listeners are sufficient.
                  // We just ensure it's muted via the API as a fallback.
                  player.setVolume(0);
              }
          }
          // --- YouTube Iframe ---
          else if (iframe.src.includes('youtube.com')) {
              onYouTubeApiReady(() => {
                  const player = new window.YT.Player(iframe, {
                      events: {
                          'onReady': (event) => {
                              event.target.mute();
                              if (isHoverPlay) {
                                  let pauseTimeout;
                                  hoverTarget.addEventListener('mouseenter', () => {
                                      clearTimeout(pauseTimeout);
                                      event.target.playVideo();
                                  });
                                  hoverTarget.addEventListener('mouseleave', () => {
                                      iframe.removeAttribute('data-playing');
                                      pauseTimeout = setTimeout(() => event.target.pauseVideo(), 1000);
                                  });
                              } else if (isInViewPlay) {
                                  setupViewportObserver(
                                      container,
                                      () => event.target.playVideo(),
                                      () => event.target.pauseVideo()
                                  );
                              } else {
                                  // For autoplaying background videos, PHP adds autoplay=1.
                                  // The onStateChange event handles the fade-in, so we just need to ensure it plays.
                                  // The observer is removed to avoid conflict with autoplay=1.
                                  event.target.playVideo();
                              }
                          },
                          'onStateChange': (event) => {
                              if (event.data === window.YT.PlayerState.PLAYING) {
                                  iframe.setAttribute('data-playing', 'true');
                              } else {
                                  iframe.removeAttribute('data-playing');
                              }
                          }
                      }
                  });
              });
          }
      }

      // --- Handle HTML5 <video> Element ---
        if (video) {
            video.addEventListener('play', () => video.setAttribute('data-playing', 'true'));
            video.addEventListener('pause', () => video.removeAttribute('data-playing'));
            video.addEventListener('ended', () => video.removeAttribute('data-playing'));

            // Check initial state in case it's already playing via `autoplay` attribute
            if (!video.paused) {
                video.setAttribute('data-playing', 'true');
            }

          if (isHoverPlay) {
              let pauseTimeout;
              hoverTarget.addEventListener('mouseenter', () => {
                  clearTimeout(pauseTimeout);
                  video.play();
              });
  
              hoverTarget.addEventListener('mouseleave', () => {
                  video.removeAttribute('data-playing');
                  pauseTimeout = setTimeout(() => {
                      video.pause();
                  }, 1000);
              });
  
              // Initially pause and ensure not marked as playing for hover-to-play
              video.pause();
              video.removeAttribute('data-playing');
          } else if (isInViewPlay) {
              video.muted = true;
              setupViewportObserver(
                  container,
                  () => video.play().catch(() => {}),
                  () => video.pause()
              );
          } else {
              // For autoplaying background videos, the `autoplay` attribute is handled by the browser.
              // The event listeners and initial state check above are sufficient.
          }
        }
    });
}

/**
 * Destroys video players within a given scope to prevent memory leaks during page transitions.
 * This is crucial for single-page applications using libraries like Barba.js.
 * @param {HTMLElement} scope - The element (typically the old Barba container) to search within for videos to destroy.
 */
export function destroyVideoPlayers(scope) {
    if (!scope) return;

    const videoContainers = scope.querySelectorAll('.video-container');

    videoContainers.forEach(container => {
        const iframe = container.querySelector('iframe');
        const video = container.querySelector('video');

        if (iframe) {
            // For Vimeo, use the API to destroy the player instance if it exists.
            if (iframe.src.includes('vimeo.com') && typeof window.Vimeo !== 'undefined' && iframe._vimeoPlayer) {
                iframe._vimeoPlayer.destroy().catch(e => console.error("Error destroying Vimeo player:", e));
            }
            // For YouTube, the API doesn't have a clean 'destroy' for the iframe itself.
            // Removing the iframe from the DOM is the most effective cleanup.
            // Barba.js handles this, but we can be explicit if needed.
            // No specific action needed here as Barba removes the container.
        }

        if (video) {
            // For HTML5 video, pause it and remove its sources to stop downloads and free memory.
            video.pause();
            video.removeAttribute('src'); // Remove src attribute
            // Remove all <source> elements
            const sources = video.querySelectorAll('source');
            sources.forEach(source => source.remove());
            // This forces the browser to release the video file.
            video.load();
        }
    });
}
